import React from 'react'
import { Icon } from '@adc/ui-morpheus'
import * as configment from '@adc/configment'
import type { Listing } from '@adc/sdk/types'

export type PartialListing = Partial<Listing> & { listing_id: string }

export interface ListingLinksProps {
  /**
   * Partial listing object containing at minimum listing_id
   */
  listing: PartialListing | null | undefined

  /**
   * Label text to display before the links
   * @default 'View Listing: '
   */
  label?: string

  /**
   * Prefix for data-elm-id attributes (for automation testing)
   * @default 'listing'
   */
  elmIdPrefix?: string

  /**
   * Whether to show the ADC link even if the listing is not published and searchable
   * @default false
   */
  showAdcLinkAlways?: boolean

  /**
   * Whether to show the Closing (Morpheus closing view) link. Should be enabled
   * only when the eval context carries an offer URN in addition to the listing.
   * The link uses the listing_id (closing views are keyed by listing ID).
   * @default false
   */
  showClosingLink?: boolean

  /**
   * style object to apply to the container div
   */
  style?: React.CSSProperties
}

const ListingLinks: React.FC<ListingLinksProps> = ({
  listing,
  label = 'View Listing: ',
  elmIdPrefix = 'listing',
  showAdcLinkAlways = false,
  showClosingLink = false,
  style = {},
}) => {
  if (!listing?.listing_id) return null

  const { listing_id, is_searchable, is_published, listing_page_path } = listing

  // Check if listing is published and searchable for ADC link
  const isPublishedAndSearchable = is_searchable && is_published

  // Get ADC host from config
  const adcHost = configment.get('AUCTIONCOM_HOST', '') as string

  // Build ADC URL using listing_page_path or fallback to /details/x-{listing_id}
  const listingPath = listing_page_path || `/details/x-${listing_id}`
  const adcUrl =
    (isPublishedAndSearchable || showAdcLinkAlways) &&
    adcHost &&
    `${adcHost}${listingPath}`

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: '8px', ...style }}
    >
      {label && <span>{label}</span>}

      {/* Internal (Morpheus) link - always shown */}
      <a
        href={`/property/listings/${listing_id}/view`}
        target='_blank'
        rel='noopener noreferrer'
        data-elm-id={`${elmIdPrefix}_morpheus_link`}
        style={{ whiteSpace: 'nowrap' }}
      >
        <Icon name='external-link' iconStyle='light' />
        Internal
      </a>

      {/* ADC link - only shown if published and searchable */}
      {adcUrl && (
        <a
          href={adcUrl}
          target='_blank'
          rel='noopener noreferrer'
          data-elm-id={`${elmIdPrefix}_adc_link`}
          style={{ whiteSpace: 'nowrap' }}
        >
          <Icon name='external-link' iconStyle='light' />
          ADC
        </a>
      )}

      {/* Closing link - only shown when an offer is present in the context.
          Uses the listing_id: closing views are keyed by listing ID. */}
      {showClosingLink && (
        <a
          href={`/property/closings/${listing_id}`}
          target='_blank'
          rel='noopener noreferrer'
          data-elm-id={`${elmIdPrefix}_closing_link`}
          style={{ whiteSpace: 'nowrap' }}
        >
          <Icon name='external-link' iconStyle='light' />
          Closing
        </a>
      )}
    </div>
  )
}

export default ListingLinks
